import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { CertificatePlatformCaBundle } from '../types/models/CertificatePlatform';
import type { ApiResponseCertificatePlatformBundle } from '../types/api/ApiResponseCertificatePlatform';

interface UsePlatformCaBundleReturn {
  bundle: CertificatePlatformCaBundle | null;
  loading: boolean;
  error: string | null;
  // HTTP status accompanying `error`, so the caller can branch per the
  // documented failure modes (405 driver unsupported, 502 upstream failure).
  // Only set when the response body parsed as JSend — a reverse-proxy error
  // page's status must not be mistaken for a documented failure mode — and
  // null for network-level failures that never produced a response.
  errorStatus: number | null;
  refetch: () => Promise<void>;
}

/**
 * Fetch a platform's live CA bundle (GET /datacenters/certificate-platforms/{id}/bundle).
 *
 * This is a live call to the upstream CA appliance and can take a couple of
 * seconds — only mount the consuming component lazily (`mountOnEnter` on the
 * parent Tabs), never fetch it eagerly with the page load. A previously
 * loaded bundle stays available while a refetch is in flight.
 */
export const usePlatformCaBundle = (platformId: number): UsePlatformCaBundleReturn => {
  const [bundle, setBundle] = useState<CertificatePlatformCaBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchBundle = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setErrorStatus(null);

      const response = await apiFetch(`/datacenters/certificate-platforms/${platformId}/bundle`);
      let body: ApiResponseCertificatePlatformBundle | null;
      try {
        body = (await response.json()) as ApiResponseCertificatePlatformBundle;
      } catch {
        body = null;
      }

      if (body?.status === 'success' && body.data?.bundle) {
        setBundle(body.data.bundle);
        return;
      }

      setError(
        body?.message ?? `Failed to load the CA bundle (HTTP ${`${response.status} ${response.statusText}`.trim()})`,
      );
      if (body !== null) {
        setErrorStatus(response.status);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching the CA bundle');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, platformId]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      void fetchBundle();
    }
  }, [auth.isAuthenticated, fetchBundle]);

  return { bundle, loading, error, errorStatus, refetch: fetchBundle };
};
